import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, ComponentSync, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import './Component_FocusedProtocol.scss';
import { ModuleFE_ProtocolInteraction } from '../../../../_enum.js';
import { ICONS } from '@app/styles';
import { ModuleFE_Advisor_Action, ModuleFE_EMInterface, ModuleFE_RuntimeOrder } from '@app/advisor-frontend';
import { ConsiderationGroupEnum, RuntimeOrderStatus_Enum, } from '@app/advisor-shared';
import { _keys, _values, asArray, dbObjectToId, scrub } from '@nu-art/ts-common';
import { getProtocolActions } from '../../../../_utils/get-protocol-actions.js';
import { ProtocolData } from '../../../../_utils/ProtocolData/ProtocolData.js';
import { Component_FocusedProtocolInfo_Mini } from '../focused-protocol-info/Component_FocusedProtocolInfo_Mini.js';
import { Component_ProtocolDetails } from '../protocol-details/Component_ProtocolDetails.js';
import { Renderer_Score } from '../../../panels/AdvisorPanel_NextSteps/Renderer_Score/Renderer_Score.js';
import { Renderer_Order_InputRequired } from '../../../panels/AdvisorPanel_NextSteps/Renderer_Order/Renderer_Order_InputRequired.js';
import { Renderer_Order_Disposition } from '../../../panels/AdvisorPanel_NextSteps/Renderer_Order/Renderer_Order_Disposition.js';
import { Renderer_Order_Group } from '../../../panels/AdvisorPanel_NextSteps/Renderer_Order/Renderer_Order_Group.js';
import { Renderer_Order_General } from '../../../panels/AdvisorPanel_NextSteps/Renderer_Order/Renderer_Order_General.js';
import { ModuleFE_AdvisorMini } from '../../../../modules/ModuleFE_AdvisorMini.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../_analytics/consts.js';
import { OrderPlacedSource } from '../../../../_analytics/types.js';
import { ModuleFE_ProvideInfo } from '../../../../modules/provide-info/ModuleFE_ProvideInfo.js';
import { contextCloseMode_Safe } from '../../../../modules/provide-info/types.js';
export class Component_FocusedProtocol_Mini extends ComponentSync {
    //######################### Life Cycle ##########################
    //Recalculate actions when the protocol changes
    __onRuntimeProtocolsUpdated = (...params) => {
        const currentProtocol = this.state.protocolData.protocol;
        const changedProtocolIds = asArray(params[1]).map(dbObjectToId);
        if (changedProtocolIds.includes(currentProtocol._id)) {
            const protocolData = ProtocolData.createNew(currentProtocol._id);
            if (!protocolData)
                return;
            this.setState({
                protocolData,
                protocolActions: getProtocolActions(currentProtocol)
            });
        }
    };
    //Recalculate actions if any of the affected orders are connected to the current protocol
    __onRuntimeOrdersUpdated = (...params) => {
        const currentProtocol = this.state.protocolData?.protocol;
        if (!currentProtocol)
            return;
        const changedOrders = asArray(params[1]);
        if (changedOrders.some(order => _keys(order.protocolMapping ?? {}).includes(currentProtocol.pathwayId)))
            this.setState({ protocolActions: getProtocolActions(currentProtocol) });
    };
    deriveStateFromProps(nextProps, state) {
        state.protocolData = nextProps.protocolData;
        state.protocolActions = getProtocolActions(state.protocolData.protocol);
        state.viewMode ??= this.inferInitialViewMode(state.protocolData, state.protocolActions);
        return state;
    }
    //######################### Logic ##########################
    openAddOrder = () => {
        const protocolId = this.state.protocolData?.protocol._id;
        if (!protocolId)
            return;
        return ModuleFE_ProtocolInteraction.openAddOrder(protocolId);
    };
    orderAll = async (e) => {
        const groups = this.state.protocolActions?.groups ?? [];
        const orders = this.state.protocolActions?.orders ?? [];
        const protocolData = this.state.protocolData;
        const groupIds = groups.map(group => group.groupId);
        const placed = ModuleFE_RuntimeOrder.resolveOrderAllRtOrders(orders.map(dbObjectToId), groupIds);
        const orderAmount = await ModuleFE_RuntimeOrder.orderAll(orders.map(dbObjectToId), groupIds);
        if (orderAmount > 0)
            placed.forEach(rtOrder => {
                const dbOrder = ModuleFE_Advisor_Action.cache.unique(rtOrder.orderId);
                ModuleFE_AdvisorAnalytics.trigger.orders.order_placed({
                    orderId: rtOrder.orderId,
                    orderLabel: dbOrder?.label ?? dbOrder?.name ?? 'N/A',
                    source: OrderPlacedSource.suggestedWorkup,
                });
            });
        ModuleFE_AdvisorAnalytics.trigger.navigation.navigation_OrderAll({
            context: 'protocol',
            orderAmount,
            protocolId: protocolData.protocol._id,
            protocolLabel: protocolData.getLabel(),
        });
    };
    unFocusProtocol = () => {
        ModuleFE_ProvideInfo.context.chain.close(contextCloseMode_Safe);
    };
    inferInitialViewMode(protocolData, protocolActions) {
        if (this.state?.viewMode === 'details')
            return 'details';
        if (!protocolActions)
            return 'graph';
        return _values(protocolActions).some(actions => !!actions.length) ? 'orders' : 'graph';
    }
    //######################### Render ##########################
    render() {
        if (!this.state.protocolData)
            return;
        if (!ModuleFE_AdvisorMini.getIsMiniActive())
            return;
        const className = _className('focused-protocol mini', this.state.viewMode);
        return _jsxs(LL_V_L, { className: className, children: [this.render_Header(), this.render_Subheader(), this.render_Orders(), this.render_Graph(), this.render_Details(), this.render_ViewModes()] });
    }
    render_Header = () => {
        const data = this.state.protocolData;
        const style = scrub({ '--dp-color': data.getColor() });
        const diagnosisLabel = data.getDiagnosisLabel();
        return _jsxs(LL_H_C, { className: 'focused-protocol__header', style: style, children: [_jsxs(LL_V_L, { className: 'focused-protocol__header__title', children: [_jsx("h1", { children: data.getLabel() }), diagnosisLabel && _jsx("h2", { style: { fontWeight: 'bold', textTransform: 'capitalize' }, children: diagnosisLabel }), _jsxs("h2", { children: [data.getConsideration() === ConsiderationGroupEnum.HIGH_CONSIDERATION ? 'High' : 'Low', " Consideration Protocol"] })] }), _jsx(ICONS.advisor_avn_close.component, { onClick: this.unFocusProtocol })] });
    };
    render_ViewModes = () => {
        return _jsxs(LL_H_C, { className: 'focused-protocol__view-modes', children: [_jsx(ICONS.advisor_v5_addOrder.component, { className: _className('orders', this.state.viewMode === 'orders' && 'active'), onClick: () => this.setState({ viewMode: 'orders' }) }), _jsx(ICONS.advisor_v5_nextSteps_graph.component, { className: _className('graph', this.state.viewMode === 'graph' && 'active'), onClick: () => this.setState({ viewMode: 'graph' }) }), _jsx(ICONS.advisor_v5_form.component, { className: _className('details', this.state.viewMode === 'details' && 'active'), onClick: () => this.setState({ viewMode: 'details' }) })] });
    };
    //######################### Render - Sub Header ##########################
    render_Subheader = () => {
        return _jsxs(LL_H_C, { className: 'focused-protocol__sub-header', children: [this.render_Subheader_Actions(), this.render_Subheader_Scores()] });
    };
    render_Subheader_Actions = () => {
        //Don't show this part at all in avatar mode or if we are post dispo
        if (ModuleFE_EMInterface.isAvatarMode() || ModuleFE_EMInterface.isPostDisposition())
            return;
        const recommendedOrders = this.state.protocolActions?.orders?.filter(order => order.orderStatus === RuntimeOrderStatus_Enum.RECOMMENDED).length ?? 0;
        const groups = this.state.protocolActions?.groups?.length ?? 0;
        const canOrderAll = (recommendedOrders + groups) > 1;
        return _jsxs(LL_H_C, { className: 'focused-protocol__sub-header__actions', children: [_jsxs(Button, { variant: 'text', onClick: this.openAddOrder, children: [_jsx(ICONS.advisor_avn_add.component, {}), "Add Order"] }), canOrderAll &&
                    _jsxs(Button, { variant: 'text', onClick: this.orderAll, children: [_jsx(ICONS.advisor_v5_addOrder.component, {}), "Order All"] })] });
    };
    render_Subheader_Scores = () => {
        const data = this.state.protocolData;
        return _jsx(LL_H_C, { className: 'focused-protocol__sub-header__scores', children: data.scoreData.map(data => _jsx(Renderer_Score, { scoreData: data }, data.getScoreReference().variableId)) });
    };
    //######################### Render - Orders ##########################
    render_Orders = () => {
        if (this.state.viewMode !== 'orders')
            return;
        const data = this.state.protocolData;
        const protocolActions = this.state.protocolActions;
        const hasDataNeeded = !!data.protocol.neededVariables?.length;
        return _jsxs(LL_H_C, { className: 'focused-protocol__orders', children: [hasDataNeeded &&
                    _jsx(Renderer_Order_InputRequired, { protocolData: data }), protocolActions.disposition.map(order => _jsx(Renderer_Order_Disposition, { protocolId: data.protocol._id, order: order }, order)), protocolActions.groups.map(group => _jsx(Renderer_Order_Group, { groupId: group.groupId, rtOrders: group.runtimeOrders }, group.groupId)), protocolActions.orders.map(order => _jsx(Renderer_Order_General, { rtOrder: order }, order._id))] });
    };
    //######################### Render - Graph ##########################
    render_Graph = () => {
        if (this.state.viewMode !== 'graph')
            return;
        return _jsx(Component_FocusedProtocolInfo_Mini, { protocolData: this.state.protocolData });
    };
    //######################### Render - Details ##########################
    render_Details = () => {
        if (this.state.viewMode !== 'details' || !this.state.protocolData)
            return;
        return _jsx(Component_ProtocolDetails, { protocolData: this.state.protocolData });
    };
}
//# sourceMappingURL=Component_FocusedProtocol_Mini.js.map